import classNames from 'classnames';
import { useMemo } from 'react';
import { Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useSelector } from 'react-redux';
import { useOrderViewerCapabilities } from '../../providers/OrderPerformanceFeaturesProvider';
import AppScrollArea from '../../components/common/AppScrollArea';
import Flex from '../../components/common/Flex';
import SubtleBadge from '../../components/common/SubtleBadge';
import { DashboardQiuckButton, PERMISSION_GOVERNANCE_QUICK_LINKS, getQuickLinkIconComponent } from '../../data/dashboard/quickLinksData';
import { resolveQuickLinkButtons, splitQuickLinkLocation } from '../../utils/dashboard/quickLinkButtons';
import { QuickLinksHeaderIconButton, QuickLinksPanelToggleIcon } from './dashboardQuickLinksHeader';

export { QuickLinksPanelToggleIcon } from './dashboardQuickLinksHeader';

const QUICK_LINK_ROLE_BADGE = {
	admin: { label: '管理员', bg: 'danger' },
	manager: { label: '总经理', bg: 'primary' },
	finance: { label: '财务', bg: 'primary' },
	buyer: { label: '采购员', bg: 'primary' },
	sale: { label: '销售员', bg: 'primary' },
	assistant: { label: '销售助理', bg: 'primary' },
	user: { label: '普通用户', bg: 'primary' },
};

export function useQuickLinkButtons() {
	const userInfo = useSelector((state) => state.auth.userInfo);
	const { canAccessRulesManagement } = useOrderViewerCapabilities();

	return useMemo(
		() =>
			resolveQuickLinkButtons(userInfo, {
				canAccessRulesManagement,
				bundles: DashboardQiuckButton,
				governance: PERMISSION_GOVERNANCE_QUICK_LINKS,
			}),
		[userInfo, canAccessRulesManagement],
	);
}

const DashboardBtnsItem = ({ button, layout = 'row', onLinkNavigate }) => {
	const isGrid = layout === 'grid';
	const itemClassName = classNames('dashboard-quick-start__item rounded-3 border position-relative', isGrid ? 'px-2 py-2' : 'px-2 px-md-3 py-2 py-md-x1 bg-body-tertiary');
	const isDisabled = !button.link || button.link === '#!' || button.link.startsWith('#');
	const colProps = isGrid ? { xs: 4 } : { xs: 6, md: 12, lg: 12, xxl: 12 };

	const itemContent = isGrid ? (
		<>
			<img src={button.img} alt={button.title || '快速链接图标'} className='dashboard-quick-start__icon flex-shrink-0' />
			<h5
				className={classNames('dashboard-quick-start__item-title fs-10 fw-semibold mb-0 text-900', {
					'hover-primary': !isDisabled,
				})}
			>
				{button.title}
			</h5>
		</>
	) : (
		<>
			<img src={button.img} alt={button.title || '快速链接图标'} className='dashboard-quick-start__icon flex-shrink-0' />
			<div className='dashboard-quick-start__text ms-2 ms-md-3 flex-grow-1 min-w-0'>
				<h5 className='dashboard-quick-start__item-title fs-9 fw-semibold mb-1 mb-md-2 text-900'>{button.title}</h5>
				<h6 className='dashboard-quick-start__item-desc mb-0 text-600 fs-10'>{button.description}</h6>
			</div>
		</>
	);

	const itemClasses = classNames(itemClassName, 'h-100 w-100', {
		'dashboard-quick-start__item--tile': isGrid,
		'dashboard-quick-start__item--disabled': isDisabled,
		'cursor-pointer hover-bg-100 transition-base': !isDisabled,
	});

	const flexProps = isGrid ? { direction: 'column', alignItems: 'center', justifyContent: 'center', className: itemClasses } : { alignItems: 'center', className: itemClasses };

	if (isDisabled) {
		return (
			<Col {...colProps} className='dashboard-quick-start__col'>
				<Flex {...flexProps} role='button' tabIndex={0}>
					{itemContent}
				</Flex>
			</Col>
		);
	}

	return (
		<Col {...colProps} className='dashboard-quick-start__col'>
			<LinkContainer to={splitQuickLinkLocation(button.link)} className='h-100 w-100 d-block' onClick={onLinkNavigate}>
				<Flex {...flexProps} role='button' tabIndex={0}>
					{itemContent}
				</Flex>
			</LinkContainer>
		</Col>
	);
};

const RailQuickLinkItem = ({ button, onLinkNavigate }) => {
	const isDisabled = !button.link || button.link === '#!' || button.link.startsWith('#');
	const IconGlyph = getQuickLinkIconComponent(button);
	const itemClassName = classNames('dashboard-quick-links-rail__item', {
		'dashboard-quick-links-rail__item--disabled': isDisabled,
	});
	const icon = <IconGlyph className='dashboard-quick-links-rail__glyph' aria-hidden='true' />;
	const tooltip = <Tooltip className='dashboard-quick-links-rail__tooltip'>{button.title}</Tooltip>;

	// OverlayTrigger 需要可挂 ref 的 DOM 节点；item 类挂在该节点上，保持 rail 直接子级动画选择器有效
	if (isDisabled) {
		return (
			<OverlayTrigger placement='right' overlay={tooltip}>
				<span className={itemClassName} aria-disabled='true' aria-label={button.title}>
					{icon}
				</span>
			</OverlayTrigger>
		);
	}

	return (
		<OverlayTrigger placement='right' overlay={tooltip}>
			<span className={itemClassName}>
				<LinkContainer to={splitQuickLinkLocation(button.link)} className='d-flex align-items-center justify-content-center w-100 h-100 text-decoration-none' onClick={onLinkNavigate}>
					<span role='button' tabIndex={0} aria-label={button.title}>
						{icon}
					</span>
				</LinkContainer>
			</span>
		</OverlayTrigger>
	);
};

const QuickOperateButton = ({ variant = 'default', onLinkNavigate, collapsed = false, onToggleCollapse }) => {
	const userInfo = useSelector((state) => state.auth.userInfo);
	const roleBadge = QUICK_LINK_ROLE_BADGE[userInfo?.role];
	const isDrawer = variant === 'drawer';
	const collapsible = !isDrawer && Boolean(onToggleCollapse);
	const isIconRail = collapsible && collapsed;
	const loadBtn = useQuickLinkButtons();

	if (!loadBtn || loadBtn.length === 0) {
		return null;
	}

	const toggleTooltip = collapsed ? '展开快速链接' : '收起为图标栏';

	if (isDrawer) {
		return (
			<Card bg={undefined} className={classNames('dashboard-quick-start dark__bg-dark dashboard-quick-start--drawer border-0 shadow-none')}>
				<Card.Body className='dashboard-quick-start__body'>
					<Row className='dashboard-quick-start__list g-2 dashboard-quick-start__list--grid'>
						{loadBtn.map((btn) => (
							<DashboardBtnsItem key={btn.link || btn.title} button={btn} layout='grid' onLinkNavigate={onLinkNavigate} />
						))}
					</Row>
				</Card.Body>
			</Card>
		);
	}

	return (
		<div
			className={classNames('dashboard-quick-start dashboard-quick-links-shell', {
				'dashboard-quick-start--chassis': !isIconRail,
				'dashboard-quick-links-shell--collapsed': isIconRail,
			})}
		>
			<div className='dashboard-quick-start__body dashboard-quick-links-shell__body'>
				<div className='dashboard-quick-links-shell__chrome'>
					<div
						className={classNames('dashboard-quick-start__header dashboard-quick-links-shell__header d-flex align-items-center', {
							'gap-1': !isIconRail,
							'justify-content-center': isIconRail,
						})}
					>
						{isIconRail ? null : (
							<div className='d-flex align-items-center gap-2 flex-grow-1 min-w-0'>
								<h5 className='dashboard-quick-start__title dashboard-quick-links-shell__title dashboard-quick-start__title--chassis fs-9 mb-0 text-truncate'>
									从这里开始您的工作
								</h5>
								{roleBadge ? (
									<SubtleBadge pill bg={roleBadge.bg} className='flex-shrink-0'>
										{roleBadge.label}
									</SubtleBadge>
								) : null}
							</div>
						)}
						<QuickLinksHeaderIconButton
							icon={<QuickLinksPanelToggleIcon isExpanded={!collapsed} />}
							label={toggleTooltip}
							onClick={onToggleCollapse}
							planB={!isIconRail}
							tooltipPlacement={collapsed ? 'right' : 'left'}
							ariaExpanded={!collapsed}
						/>
					</div>

					<div className='dashboard-quick-links-shell__viewport'>
						{isIconRail ? (
							<div className='dashboard-quick-links-shell__rail-scroll'>
								<div className='dashboard-quick-links-shell__rail'>
									<hr className='dashboard-quick-links-rail__divider' aria-hidden='true' />
									<div className='dashboard-quick-links-rail__items'>
										{loadBtn.map((btn) => (
											<RailQuickLinkItem key={btn.link || btn.title} button={btn} onLinkNavigate={onLinkNavigate} />
										))}
									</div>
								</div>
							</div>
						) : (
							<AppScrollArea className='dashboard-quick-links-shell__scroll app-scroll-area--embedded'>
								<div className='dashboard-quick-links-shell__expanded'>
									<Row className='dashboard-quick-start__list g-2 g-md-3'>
										{loadBtn.map((btn) => (
											<DashboardBtnsItem key={btn.link || btn.title} button={btn} layout='row' onLinkNavigate={onLinkNavigate} />
										))}
									</Row>
								</div>
							</AppScrollArea>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default QuickOperateButton;
